import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Truck, CheckCircle2, XCircle, Phone, MapPin, LogOut } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLOR = {
  "Rider Assigned": "bg-blue-50 text-blue-700",
  "Out for Delivery": "bg-amber-50 text-amber-700",
  Delivered: "bg-green-50 text-green-700",
  "Failed Delivery": "bg-red-50 text-red-600",
};

function Stat({ label, value, Icon, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3" data-testid={`rider-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-[11px] text-stone-400 uppercase tracking-wider">{label}</p>
        <p className="font-display text-2xl font-bold text-stone-900">{value}</p></div>
    </div>
  );
}

export default function RiderHub() {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState({ total: 0, pending_pickup: 0, out_for_delivery: 0, completed: 0, failed: 0 });
  const [orders, setOrders] = useState([]);

  const load = () => {
    API.get("/rider/summary").then((r) => setSummary(r.data)).catch(() => {});
    API.get("/rider/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    try { await API.put(`/rider/orders/${o.id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const active = orders.filter((o) => ["Rider Assigned", "Out for Delivery"].includes(o.status));
  const done = orders.filter((o) => ["Delivered", "Failed Delivery"].includes(o.status));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="rider-hub">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Suvikka Rider</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">Delivery Dashboard</h1>
          <p className="text-stone-500 mt-1">{user?.name} · {user?.employee_id} · {user?.assigned_city || "—"}</p>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-[#6A1B29]" data-testid="rider-logout">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat label="Pending Pickup" value={summary.pending_pickup} Icon={Package} color="bg-blue-50 text-blue-600" />
        <Stat label="Out for Delivery" value={summary.out_for_delivery} Icon={Truck} color="bg-amber-50 text-amber-600" />
        <Stat label="Completed" value={summary.completed} Icon={CheckCircle2} color="bg-green-50 text-green-600" />
        <Stat label="Failed" value={summary.failed} Icon={XCircle} color="bg-red-50 text-red-600" />
      </div>

      <h2 className="font-display text-xl font-semibold text-stone-900 mt-10 mb-3">Active Deliveries</h2>
      <div className="space-y-3" data-testid="rider-active-orders">
        {active.length === 0 && <p className="text-sm text-stone-500 bg-white border border-stone-200 rounded-xl p-6">No active deliveries assigned right now.</p>}
        {active.map((o) => (
          <div key={o.id} className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5" data-testid={`rider-order-${o.order_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-stone-900">{o.order_id}</p>
                <p className="text-sm text-stone-700 mt-1 font-medium">{o.customer_name}</p>
                <a href={`tel:${o.phone}`} className="inline-flex items-center gap-1.5 text-sm text-[#6A1B29] mt-1"><Phone className="w-3.5 h-3.5" /> {o.phone}</a>
                <p className="flex items-start gap-1.5 text-sm text-stone-600 mt-1.5 max-w-md"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {o.address}</p>
              </div>
              <div className="text-right">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status]}`}>{o.status}</span>
                <p className="font-semibold text-stone-900 mt-2">{inr(o.amount)}</p>
                <p className="text-xs text-stone-400">{o.payment_method}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-2">
              <p className="text-xs text-stone-500 mr-auto self-center">{o.items.map((i) => `${i.title} ×${i.qty}`).join(", ")}</p>
              {o.status === "Rider Assigned" && (
                <button onClick={() => setStatus(o, "Out for Delivery")} className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg" data-testid={`rider-pickup-${o.order_id}`}>Picked Up / Out for Delivery</button>
              )}
              {o.status === "Out for Delivery" && (<>
                <button onClick={() => setStatus(o, "Delivered")} className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg" data-testid={`rider-deliver-${o.order_id}`}>Delivered</button>
                <button onClick={() => setStatus(o, "Failed Delivery")} className="text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg" data-testid={`rider-fail-${o.order_id}`}>Delivery Failed</button>
              </>)}
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-semibold text-stone-900 mt-10 mb-3">Completed & Past</h2>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto" data-testid="rider-past-orders">
        <table className="w-full text-sm min-w-[520px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
            <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {done.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-stone-500">No completed deliveries yet.</td></tr>}
            {done.map((o) => (
              <tr key={o.id} className="border-b border-stone-100">
                <td className="p-3 font-medium">{o.order_id}</td>
                <td className="p-3 text-stone-600">{o.customer_name}</td>
                <td className="p-3">{inr(o.amount)}</td>
                <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status]}`}>{o.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
